import { urlConfig } from 'config/url-config';
import { CollectionHelpers } from 'src/helpers/CollectionHelpers';

import {
    CompanyProfile,
    Event,
    IntercomChannel,
    QrScan,
    QrScanPayload,
    QrScanSource,
    QrScanType,
    UserReferrer,
} from 'models';
import { BoothHelpers } from 'helpers/BoothHelpers';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import type { QrPoster, QrPosterWindow } from 'helpers/QrPosterHelpers';

import { CompanyProfileService } from 'services/CompanyProfileService';

export type LeadActivity<Scan extends QrScan> =
    | { at: string; scan: Scan; chat?: undefined }
    | { at: string; chat: IntercomChannel; scan?: undefined };

export type QrScanLead<Scan extends QrScan> = {
    key: string;
    userId?: string;
    userInfo?: QrScan['userInfo'];
    scans: Scan[];
    chats: IntercomChannel[];
    activities: LeadActivity<Scan>[];
};

const QrScanHelpers = {
    urls: {
        scan: (type: QrScanType, id: string) => `/scan/${type}/${id}`,
        // Every scan is a visit at an RE+ event, and the RE+ referrer keeps the visitor in the Showtime experience
        withScanGate: (url: string, params: Record<string, string> = {}) =>
            `${url}?${new URLSearchParams({ [QrScanHelpers.gateParam]: '1', referrer: UserReferrer.REPLUS, ...params })}`,

        profileScanLanding: (
            company: { id: string; slug?: string | null },
            { userType = 'designer', params = {} }: { userType?: string; params?: Record<string, string> } = {},
        ) =>
            QrScanHelpers.urls.withScanGate(CompanyHelpers.urls.view(company.slug ?? company.id), {
                type: userType,
                ...params,
            }),
    },

    gateParam: 'qr',

    // Exhibitors scan their own codes to try them; those scans must not become leads
    testParam: 'test',

    isQrScan: (scan: Pick<QrScan, 'source'>) => scan.source !== QrScanSource.WEB,

    // Scans created before the type field existed have no type and were all profile scans
    isProfileScan: (scan: Pick<QrScan, 'type'>) => !scan.type || scan.type === QrScanType.PROFILE,

    // The visitor's most recent save of a profile (by company id), brochure or product, so every entry point opens the
    // same notes
    findLatestScan: (scans: QrScanPayload[], type: QrScanType, itemId: string) => {
        const isThisItem = (scan: QrScanPayload) =>
            ({
                [QrScanType.PROFILE]: QrScanHelpers.isProfileScan(scan) && scan.company === itemId,
                [QrScanType.COLLATERAL]: scan.collateral === itemId,
                [QrScanType.PRODUCT]: scan.product === itemId,
            })[type];

        return scans.filter(isThisItem).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
    },

    isLocked: (lead: Pick<QrScanLead<QrScan>, 'scans' | 'chats'>) =>
        [...lead.scans, ...lead.chats].every((item) => item.lockedPreview),

    visitorKey: (scan: QrScan) =>
        scan.user ?? scan.userInfo?.email?.toLowerCase() ?? scan.lockedPreview?.visitor ?? scan.id ?? '',

    groupByVisitor: <Scan extends QrScan>(scans: Scan[], chats: IntercomChannel[] = []): QrScanLead<Scan>[] => {
        const activitiesByVisitor = new Map<string, LeadActivity<Scan>[]>();

        const add = (key: string, activity: LeadActivity<Scan>) => {
            activitiesByVisitor.set(key, [...(activitiesByVisitor.get(key) ?? []), activity]);
        };

        for (const scan of scans) {
            add(QrScanHelpers.visitorKey(scan), { at: String(scan.createdAt ?? ''), scan });
        }

        for (const chat of chats) {
            add(chat.createdBy || chat.lockedPreview?.visitor || chat.id, {
                at: new Date(chat.latestMessage?.createdAt ?? chat.createdAt).toISOString(),
                chat,
            });
        }

        return [...activitiesByVisitor]
            .map(([key, visitorActivities]) => {
                const activities = [...visitorActivities].sort((a, b) => b.at.localeCompare(a.at));
                const visitorScans = activities.flatMap(({ scan }) => (scan ? [scan] : []));
                const visitorChats = activities.flatMap(({ chat }) => (chat ? [chat] : []));

                return {
                    key,
                    userId: visitorScans.find((scan) => scan.user)?.user ?? visitorChats[0]?.createdBy,
                    userInfo: visitorScans.find((scan) => scan.userInfo?.email)?.userInfo,
                    scans: visitorScans,
                    chats: visitorChats,
                    activities,
                };
            })
            .sort((a, b) => b.activities[0].at.localeCompare(a.activities[0].at));
    },

    generateDataURL: async (path: string) => {
        const { toDataURL } = await import('qrcode');

        return toDataURL(`${urlConfig.frontend}${path}`, { width: 2048, margin: 4 });
    },

    poster: async (
        company: CompanyProfile,
        event: Event | undefined,
        item: { type: QrScanType.PROFILE | QrScanType.PRODUCT; id: string; productName?: string } = {
            type: QrScanType.PROFILE,
            id: company.slug ?? company.id,
        },
    ): Promise<QrPoster> => {
        const logoOf = ({ logos }: CompanyProfile) => logos.small || logos.large;
        // A depth 0 load of the profile has only file ids here, and the poster needs the file url
        const logo =
            typeof logoOf(company) === 'string' ? logoOf(await CompanyProfileService.get(company.id)) : logoOf(company);
        const booth = event && CollectionHelpers.find(event.companies, company.id, 'company')?.booth;

        return {
            type: item.type,
            companyName: company.name,
            productName: item.productName,
            logoUrl: typeof logo === 'object' ? logo?.url : null,
            scanUrl: `${urlConfig.frontend}${QrScanHelpers.urls.scan(item.type, item.id)}`,
            booth: booth || undefined,
            hall: event && booth ? BoothHelpers.getHall(booth, event.boothPatterns, event.name).hallName : undefined,
        };
    },

    // Prints from a Letter-size frame, which keeps the app page out of the print and lays the posters out at print size
    // Also the file name Chrome suggests for "Save as PDF"
    posterTitle: (posters: QrPoster[], eventName?: string) => {
        const [poster] = posters;
        const subject =
            posters.length === 1
                ? [poster.companyName, poster.productName, 'QR poster']
                : [`QR posters (${posters.length})`];

        return [eventName, ...subject]
            .filter(Boolean)
            .join(' ')
            .replace(/[/\\:*?"<>|]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    },

    printPosters: async (posters: QrPoster[], eventName?: string) => {
        const { QrPosterHelpers } = await import('helpers/QrPosterHelpers');
        const title = QrScanHelpers.posterTitle(posters, eventName);

        const iframe = document.createElement('iframe');
        Object.assign(iframe.style, {
            position: 'fixed',
            left: '-10000px',
            top: '0',
            width: '8.5in',
            height: '11in',
            border: '0',
        });
        const posterHtml = QrPosterHelpers.document(posters, {
            title,
            replusLogoUrl: `${window.location.origin}/images/resourcepro-logo-white.svg`,
        });
        // Its own document URL, unlike srcdoc's about:srcdoc, so references inside resolve the same in every browser.
        // Relative URLs would resolve against the blob URL, hence the absolute logo URL.
        const posterUrl = URL.createObjectURL(new Blob([posterHtml], { type: 'text/html' }));
        iframe.src = posterUrl;

        const loaded = new Promise((resolve) => iframe.addEventListener('load', resolve, { once: true }));
        document.body.append(iframe);
        await loaded;

        const posterWindow = iframe.contentWindow as QrPosterWindow;
        await posterWindow.qrPostersReady;

        // Chrome suggests the file name from the app page's title rather than the poster's, so it carries the title
        // while the dialog is open
        const appTitle = document.title;
        const cleanup = new AbortController();
        const restore = () => {
            cleanup.abort();
            document.title = appTitle;
            iframe.remove();
            URL.revokeObjectURL(posterUrl);
        };

        posterWindow.addEventListener('afterprint', restore, { signal: cleanup.signal });
        // In case a browser skips afterprint: the next click in the app means the dialog has closed
        window.addEventListener('pointerdown', restore, { signal: cleanup.signal });
        document.title = title;
        posterWindow.print();
    },
};

export { QrScanHelpers };
